import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, CalendarClock, CreditCard, Landmark, Layers, MapPin, PiggyBank, RotateCcw, Search, Sprout, Users, Warehouse, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TIERS } from '../utils/sector';

const TERMS = [6, 12, 24, 36];
const SHORT = { 대구광역시: '대구', 경상북도: '경북' };
const norm = s => s.replace(/\s+/g, '').toLowerCase();

// 시·군·구와 읍·면·동을 한 줄 검색 목록으로 펼칩니다. (예: '경북 경산시 하양읍')
function useRegionIndex(regions) {
  return useMemo(() => {
    const out = [];
    for (const s of regions?.sido || []) for (const g of s.g) {
      out.push({ key: g.id, label: `${SHORT[s.n] || s.n} ${g.n}`, sel: { sido: s.id, sgg: g.id, emd: '' } });
      for (const e of g.e) out.push({ key: e.id, label: `${SHORT[s.n] || s.n} ${g.n} ${e.n}`, sel: { sido: s.id, sgg: g.id, emd: e.id } });
    }
    return out.map(x => ({ ...x, n: norm(x.label) }));
  }, [regions]);
}

// 지역 입력 + 펼침 패널 안에 바로 붙는 추천 목록
function RegionField({ index, value, onPick, onEnter }) {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  const [focus, setFocus] = useState(false);
  const [cursor, setCursor] = useState(0);
  const input = useRef(null);
  useEffect(() => setText(value), [value]);
  useEffect(() => { const id = setTimeout(() => input.current?.focus(), 60); return () => clearTimeout(id); }, []);
  const q = norm(text === value ? '' : text);
  const matches = useMemo(() => {
    if (!q) return [];
    return index.filter(x => x.n.includes(q)).sort((a, b) => a.n.indexOf(q) - b.n.indexOf(q) || a.n.length - b.n.length).slice(0, 6);
  }, [index, q]);
  const open = focus && matches.length > 0;
  const pick = item => { onPick(item); setText(item.label); setFocus(false); input.current?.blur(); };
  return <div className="bp-region">
    <div className={`bp-search ${focus ? 'is-focus' : ''}`}>
      <MapPin size={17}/>
      <input ref={input} id="region-input" autoComplete="off" role="combobox" aria-expanded={open} aria-controls="region-list" aria-label={t('map.region')}
        placeholder={t('map.regionPlaceholder')} value={text}
        onFocus={e => { setFocus(true); e.target.select(); }} onBlur={() => setTimeout(() => setFocus(false), 120)}
        onChange={e => { setText(e.target.value); setCursor(0); }}
        onKeyDown={e => {
          if (e.nativeEvent.isComposing || e.keyCode === 229) return; // 한글 조합 중 Enter/방향키 무시
          if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, matches.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
          if (e.key === 'Enter') { e.preventDefault(); if (open && matches[cursor]) pick(matches[cursor]); else onEnter(); }
        }}/>
      {text && <button className="bp-clear" aria-label={t('common.clear')} onMouseDown={e => { e.preventDefault(); setText(''); input.current?.focus(); }}><X size={14}/></button>}
    </div>
    {open && <ul id="region-list" className="bp-suggest" role="listbox">
      {matches.map((m, i) => { const parts = m.label.split(' '); const last = parts.pop();
        return <li key={m.key} role="option" aria-selected={i === cursor} className={i === cursor ? 'on' : ''} onMouseDown={e => { e.preventDefault(); pick(m); }} onMouseEnter={() => setCursor(i)}>
          <span className={`bp-pin ${m.sel.emd ? 'emd' : ''}`}><MapPin size={14}/></span><strong>{last}</strong><small>{parts.join(' ')}</small></li>; })}
    </ul>}
  </div>;
}

function Chips({ icon: Icon, label, value, options, onChange }) {
  return <div className="bp-row"><span className="bp-label"><Icon size={14}/>{label}</span>
    <div className="bp-chips" role="radiogroup" aria-label={label}>{options.map(o => { const OIcon = o.icon;
      return <button key={o.value} role="radio" aria-checked={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>{OIcon && <OIcon size={14}/>}{o.label}</button>; })}</div></div>;
}

const TIER_ICONS = { 전체: Layers, '1금융': Landmark, 저축은행: Building2, 신협: Users, 새마을금고: Warehouse, 농협: Sprout, ATM: CreditCard };

// 지도 상단 검색바: 접힌 알약 → 클릭하면 아래로 펼쳐지는 검색 패널 (지역·상품·금융권·기간)
export default function ExploreBar({ regions, regionLabel, onPickRegion, kind, setKind, tier, setTier, term, setTerm, onSearch, canSearch }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const index = useRegionIndex(regions);
  useEffect(() => {
    if (!open) return;
    const key = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [open]);
  const tierLabel = t(TIERS.find(([v]) => v === tier)?.[1] || (tier === 'ATM' ? 'map.atmOnly' : 'common.all'));
  const filters = [t(kind === 'deposit' ? 'common.deposit' : 'common.saving'), tierLabel, `${term}${t('common.months')}`].join(' · ');
  const submit = () => { if (canSearch) { onSearch(); setOpen(false); } };
  const reset = () => { setKind('deposit'); setTier('전체'); setTerm(12); };
  return <div className={`explore-bar ${open ? 'is-open' : ''}`}>
    {open && <div className="bar-backdrop" onClick={() => setOpen(false)} aria-hidden="true"/>}
    {!open ? <button className="bar-summary" onClick={() => setOpen(true)} aria-expanded="false" aria-label={t('map.filters')}>
      <img className="bar-brand" src="/brand/logo64.png" alt="" width="32" height="32"/>
      <span className="bar-text"><span className="bar-region">{regionLabel || t('map.regionPlaceholder')}</span><span className="bar-sep"/><span className="bar-filters">{filters}</span></span>
      <span className="bar-go"><Search size={15} strokeWidth={2.4}/></span>
    </button> : <div className="bar-panel" role="search">
      <div className="bp-top">
        <img className="bp-brand" src="/brand/logo64.png" alt="" width="34" height="34"/>
        <RegionField index={index} value={regionLabel || ''} onPick={onPickRegion} onEnter={submit}/>
        <button className="bp-close icon-btn" onClick={() => setOpen(false)} aria-label={t('common.close')}><X size={18}/></button>
      </div>
      <Chips icon={PiggyBank} label={t('map.productType')} value={kind} onChange={setKind} options={[{ value: 'deposit', label: t('common.deposit'), icon: PiggyBank }, { value: 'saving', label: t('common.saving'), icon: CalendarClock }]}/>
      <Chips icon={Landmark} label={t('map.sector')} value={tier} onChange={setTier} options={[...TIERS, ['ATM', 'map.atmOnly']].map(([v, k]) => ({ value: v, label: t(k), icon: TIER_ICONS[v] }))}/>
      <Chips icon={CalendarClock} label={t('product.term')} value={term} onChange={v => setTerm(Number(v))} options={TERMS.map(m => ({ value: m, label: `${m}${t('common.months')}` }))}/>
      <div className="bp-foot">
        <button className="bp-reset" onClick={reset}><RotateCcw size={14}/>{t('common.reset')}</button>
        <button className="bp-submit" onClick={submit} disabled={!canSearch}><Search size={17} strokeWidth={2.6}/>{t('map.searchRegion')}</button>
      </div>
    </div>}
  </div>;
}
